import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Box, Typography, Switch, IconButton, TextField, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Tooltip, Chip,
    Collapse
} from '@mui/material';
import { TabPanel } from '@mui/lab';
import { Edit, Save, Cancel, Receipt, Delete, Add, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import styled from '@emotion/styled';
import { showAlert } from '../redux/slices/alerts';
import ConfirmDialog from './admin/ConfirmDialog';
import {
    getBillingEmployees,
    updateEmployeeSalaryData,
    setEmployeeTerminated,
    getEmployeePayments,
    createEmployeePayment,
    updateEmployeePayment,
    deleteEmployeePayment
} from '../redux/ducks/billingEmployees';
import { activateUser, deactivateUser } from '../redux/slices/users';
import { useHasPermission } from '../utils/permissionUtils';
import AppDatePicker from './AppDatePicker';
import DeactivateUserDialog from './DeactivateUserDialog';
import { formatDate, toLocalDay } from '../utils/dateUtils';

const SalaryEditorGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr) auto',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    alignItems: 'center',
}));

const ActionsCluster = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
}));

const PaymentFormGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
}));

const PaymentsHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const CenteredLoading = styled(Box, { shouldForwardProp: (p) => p !== "$large" })(({ theme, $large }) => ({
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing($large ? 8 : 4),
}));

const EmptyTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyHeaderCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
}));

const EmployeeRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== 'inactive' })(({ inactive }) => ({
    opacity: inactive ? 0.55 : 1,
}));

const EmployeeNameLink = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const EmployeesTabPanel = styled(TabPanel)({
    padding: 0,
});

const InactivePaper = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const InactiveToggle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
}));

const PAYMENT_TYPES = ['Salary', 'Bonus', 'Overtime', 'Commission'];
const fmtMoney = (v) => `$ ${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => formatDate(d);

const computeWage = (salary, hours) => {
    const s = Number(salary) || 0;
    const h = Number(hours) || 0;
    return h > 0 ? s / h : 0;
};

const SalaryEditor = ({ employee, onSaved, onCancel, colSpan = 9 }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        salary: employee.salaryData?.salary ?? '',
        monthlyHours: employee.salaryData?.monthlyHours ?? '',
        dailyHours: employee.salaryData?.dailyHours ?? '',
        hiredAt: fmtDate(employee.salaryData?.hiredAt),
        terminatedAt: fmtDate(employee.salaryData?.terminatedAt),
    });
    const [saving, setSaving] = useState(false);
    const wage = useMemo(() => computeWage(form.salary, form.monthlyHours), [form.salary, form.monthlyHours]);

    const save = () => {
        setSaving(true);
        const salaryData = {
            salary: form.salary === '' ? 0 : Number(form.salary),
            monthlyHours: form.monthlyHours === '' ? 0 : Number(form.monthlyHours),
            dailyHours: form.dailyHours === '' ? 0 : Number(form.dailyHours),
            hiredAt: form.hiredAt || null,
            terminatedAt: form.terminatedAt || null,
        };
        dispatch(updateEmployeeSalaryData(employee._id, salaryData, (resp) => {
            setSaving(false);
            if (resp?.ok) {
                dispatch(showAlert({ severity: 'success', message: 'Salary data saved' }));
                onSaved(resp.data.salaryData);
            }
        }));
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <TableRow>
            <TableCell colSpan={colSpan}>
                <SalaryEditorGrid>
                    <TextField label="Salary (monthly)" type="number" size="small" value={form.salary} onChange={set('salary')} />
                    <TextField label="Monthly Hours" type="number" size="small" value={form.monthlyHours} onChange={set('monthlyHours')} />
                    <TextField label="Daily Hours" type="number" size="small" value={form.dailyHours} onChange={set('dailyHours')} />
                    <AppDatePicker label="Hired" size="small" value={form.hiredAt} onChange={(v) => setForm({ ...form, hiredAt: v })} />
                    <AppDatePicker label="Terminated" size="small" value={form.terminatedAt} onChange={(v) => setForm({ ...form, terminatedAt: v })} />
                    <ActionsCluster>
                        <Chip label={`Hourly: ${fmtMoney(wage)}`} size="small" color="info" variant="outlined" />
                        <IconButton color="primary" onClick={save} disabled={saving}><Save fontSize="small" /></IconButton>
                        <IconButton onClick={onCancel} disabled={saving}><Cancel fontSize="small" /></IconButton>
                    </ActionsCluster>
                </SalaryEditorGrid>
            </TableCell>
        </TableRow>
    );
};

const PaymentDialog = ({ open, onClose, employee, payment, onSaved }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({ amount: '', date: '', type: 'Salary', notes: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (payment) {
            setForm({
                amount: payment.amount ?? '',
                date: fmtDate(payment.date),
                type: payment.type || 'Salary',
                notes: payment.notes || '',
            });
        } else {
            setForm({ amount: '', date: toLocalDay(), type: 'Salary', notes: '' });
        }
    }, [payment, open]);

    const save = () => {
        setSaving(true);
        const body = { amount: Number(form.amount), date: form.date, type: form.type, notes: form.notes };
        const onResp = (resp) => {
            setSaving(false);
            if (resp?.ok) {
                dispatch(showAlert({ severity: 'success', message: payment ? 'Payment updated' : 'Payment recorded' }));
                onSaved(resp.data);
                onClose();
            }
        };
        dispatch(payment
            ? updateEmployeePayment(employee._id, payment._id, body, onResp)
            : createEmployeePayment(employee._id, body, onResp));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{payment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
            <DialogContent>
                <PaymentFormGrid>
                    <TextField label="Type" select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                        {PAYMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <TextField label="Amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    <AppDatePicker label="Date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} fullWidth={false} size="medium" />
                    <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </PaymentFormGrid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving || !form.amount || !form.date}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

const PaymentsDialog = ({ open, onClose, employee }) => {
    const dispatch = useDispatch();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = () => {
        if (!employee) return;
        setLoading(true);
        dispatch(getEmployeePayments(employee._id, (resp) => {
            setLoading(false);
            if (resp?.ok) setPayments(resp.data);
        }));
    };

    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, employee?._id]);

    const remove = (p) => setPendingDelete(p);

    const confirmRemove = () => {
        const p = pendingDelete;
        setPendingDelete(null);
        if (!p) return;
        dispatch(deleteEmployeePayment(employee._id, p._id, (resp) => {
            if (resp?.ok) {
                dispatch(showAlert({ severity: 'success', message: 'Payment deleted' }));
                load();
            }
        }));
    };

    const total = payments.reduce((s, p) => s + (p.amount || 0), 0);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    Payments — {employee?.first_name} {employee?.last_name}
                </DialogTitle>
                <DialogContent>
                    <PaymentsHeaderRow>
                        <Typography variant="body2" color="text.secondary">
                            {payments.length} record(s) · Total {fmtMoney(total)}
                        </Typography>
                        <Button startIcon={<Add />} variant="contained" size="small" onClick={() => { setEditing(null); setEditorOpen(true); }}>
                            Record Payment
                        </Button>
                    </PaymentsHeaderRow>
                    {loading ? (
                        <CenteredLoading><CircularProgress /></CenteredLoading>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Notes</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.map(p => (
                                        <TableRow key={p._id} hover>
                                            <TableCell>{fmtDate(p.date)}</TableCell>
                                            <TableCell><Chip size="small" label={p.type} /></TableCell>
                                            <TableCell align="right">{fmtMoney(p.amount)}</TableCell>
                                            <TableCell>{p.notes}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => { setEditing(p); setEditorOpen(true); }}><Edit fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => remove(p)}><Delete fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {payments.length === 0 && (
                                        <TableRow><EmptyTableCell colSpan={5} align="center">No payments recorded</EmptyTableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
            <PaymentDialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                employee={employee}
                payment={editing}
                onSaved={load}
            />
            <ConfirmDialog
                open={Boolean(pendingDelete)}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmRemove}
                title="Delete payment"
                message={pendingDelete ? `Delete ${pendingDelete.type} payment of ${fmtMoney(pendingDelete.amount)} on ${fmtDate(pendingDelete.date)}? This cannot be undone.` : ''}
            />
        </>
    );
};

const BillingEmployees = ({ tabValue }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canToggleActive = hasPermission('activateUsers') && hasPermission('deactivateUsers');
    const currentUserId = useSelector((state) => state.auth.userId);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [paymentsFor, setPaymentsFor] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState(null);

    const load = () => {
        setLoading(true);
        dispatch(getBillingEmployees((data) => {
            setLoading(false);
            if (data) setEmployees(data);
        }));
    };

    useEffect(() => { if (tabValue === 'employees') load(); /* eslint-disable-next-line */ }, [tabValue]);

    // Optimistic toggle helpers — flip locally first, revert on failure.
    const applyActive = (emp, value) => {
        setEmployees(es => es.map(e => e._id === emp._id ? { ...e, active: value } : e));
        const callback = (result) => {
            if (!result) {
                setEmployees(es => es.map(x => x._id === emp._id ? { ...x, active: emp.active } : x));
            }
        };
        dispatch(value
            ? activateUser({ userId: emp._id, callback })
            : deactivateUser({ userId: emp._id, callback }));
    };

    const toggleActive = (emp, value) => {
        if (value) {
            applyActive(emp, true);
        } else {
            setDeactivateTarget(emp);
        }
    };

    const toggleTerminated = (emp, value) => {
        const newDate = value ? toLocalDay() : null;
        const prev = emp.salaryData?.terminatedAt || null;
        setEmployees(es => es.map(e => e._id === emp._id ? { ...e, salaryData: { ...e.salaryData, terminatedAt: newDate } } : e));
        dispatch(setEmployeeTerminated(emp._id, newDate, (resp) => {
            if (!resp?.ok) {
                setEmployees(es => es.map(x => x._id === emp._id ? { ...x, salaryData: { ...x.salaryData, terminatedAt: prev } } : x));
            }
        }));
    };

    const onSalarySaved = (empId, salaryData) => {
        setEmployees(es => es.map(e => e._id === empId ? { ...e, salaryData } : e));
        setEditingId(null);
    };

    const totalCols = 9;

    const renderRow = (emp) => {
        const isEditing = editingId === emp._id;
        const isSelf = emp._id === currentUserId;
        const sd = emp.salaryData || {};
        const terminated = !!sd.terminatedAt;
        if (isEditing) {
            return (
                <SalaryEditor
                    key={emp._id}
                    employee={emp}
                    onSaved={(salaryData) => onSalarySaved(emp._id, salaryData)}
                    onCancel={() => setEditingId(null)}
                    colSpan={totalCols}
                />
            );
        }
        return (
            <EmployeeRow key={emp._id} hover inactive={!emp.active}>
                <TableCell>
                    <EmployeeNameLink
                        variant="body2"
                        component={RouterLink}
                        to={`/user/${emp._id}`}
                    >
                        {emp.first_name} {emp.last_name}
                    </EmployeeNameLink>
                    <Typography variant="caption" color="text.secondary" display="block">{emp.email}</Typography>
                </TableCell>
                <TableCell>{emp.title}</TableCell>
                <TableCell align="right">{fmtMoney(sd.salary)}</TableCell>
                <TableCell align="right">{fmtMoney(sd.hourlyWage)}</TableCell>
                <TableCell>
                    <Typography variant="caption" display="block">
                        In: {fmtDate(sd.hiredAt) || 'On file pending'}
                    </Typography>
                    <Typography variant="caption" display="block" color={terminated ? 'error.main' : 'text.secondary'}>
                        Out: {fmtDate(sd.terminatedAt) || (emp.active ? 'Currently employed' : 'On file pending')}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    {canToggleActive && !isSelf ? (
                        <Switch size="small" checked={!!emp.active} onChange={(e) => toggleActive(emp, e.target.checked)} />
                    ) : (
                        <Chip size="small" variant="outlined" color={emp.active ? 'success' : 'default'} label={emp.active ? 'Active' : 'Inactive'} />
                    )}
                </TableCell>
                <TableCell align="center">
                    <Tooltip title={terminated ? `Terminated ${fmtDate(sd.terminatedAt)}` : 'Not terminated'}>
                        <Switch size="small" color="error" checked={terminated} onChange={(e) => toggleTerminated(emp, e.target.checked)} />
                    </Tooltip>
                </TableCell>
                <TableCell align="right">
                    {fmtMoney(emp.totalPaid)}
                    <Typography variant="caption" display="block" color="text.secondary">{emp.paymentCount} payment(s)</Typography>
                </TableCell>
                <TableCell align="right">
                    <Tooltip title="Edit salary"><IconButton size="small" onClick={() => setEditingId(emp._id)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Payments"><IconButton size="small" onClick={() => setPaymentsFor(emp)}><Receipt fontSize="small" /></IconButton></Tooltip>
                </TableCell>
            </EmployeeRow>
        );
    };

    const headerRow = (
        <TableHead>
            <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Title</TableCell>
                <TableCell align="right">Salary</TableCell>
                <TableCell align="right">Hourly</TableCell>
                <TableCell>Hired / Term.</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Terminated</TableCell>
                <TableCell align="right">Total Paid</TableCell>
                <TableCell align="right">Actions</TableCell>
            </TableRow>
        </TableHead>
    );

    const active = employees.filter(e => e.active);
    const inactive = employees.filter(e => !e.active);

    return (
        <EmployeesTabPanel value="employees">
            <Paper>
                <TableContainer>
                    {loading ? (
                        <CenteredLoading $large><CircularProgress /></CenteredLoading>
                    ) : (
                        <Table stickyHeader size="small">
                            {headerRow}
                            <TableBody>
                                {active.map(renderRow)}
                                {active.length === 0 && (
                                    <TableRow><EmptyHeaderCell colSpan={totalCols} align="center">No active employees</EmptyHeaderCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Paper>

            {!loading && inactive.length > 0 && (
                <InactivePaper>
                    <InactiveToggle onClick={() => setShowInactive(s => !s)}>
                        {showInactive ? <ExpandLess /> : <ExpandMore />}
                        <Typography variant="subtitle2">Inactive employees ({inactive.length})</Typography>
                    </InactiveToggle>
                    <Collapse in={showInactive} unmountOnExit>
                        <TableContainer>
                            <Table size="small">
                                {headerRow}
                                <TableBody>
                                    {inactive.map(renderRow)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Collapse>
                </InactivePaper>
            )}

            <PaymentsDialog
                open={!!paymentsFor}
                onClose={() => { setPaymentsFor(null); load(); }}
                employee={paymentsFor}
            />

            <DeactivateUserDialog
                open={!!deactivateTarget}
                user={deactivateTarget}
                onClose={() => setDeactivateTarget(null)}
                onConfirm={() => applyActive(deactivateTarget, false)}
            />
        </EmployeesTabPanel>
    );
};

export default BillingEmployees;
